// /settings → "Амралтын календарь" tab (A4). Full CRUD via inline actions.
// Also exposes "Ажлын хуваарь" card (workingDays / workingHours / orderCutoffTime).

import {
  Anchor,
  Badge,
  Box,
  Card,
  Chip,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core"
import { ListState } from "../ui"
import { SETTINGS_WRITE, SettingsActionCell, SettingsButton, SettingsRowAction } from "./settings-controls"
import { TimeInput } from "@mantine/dates"
import { useForm } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { GovernanceConfigApi, HolidayCalendarApi } from "../../apis"
import type { IBusinessHoliday } from "../../apis/holiday-calendar"
import RenderText from "../ui/render-text"
import { dateFormat } from "../../utils/date"
import { HolidayFormModal } from "./HolidayFormModal"
import { useSettingsMutations } from "./hooks"

type WorkDay = "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT" | "SUN"

const ALL_DAY_VALUES: { value: WorkDay; dayKey: string }[] = [
  { value: "MON", dayKey: "mon" },
  { value: "TUE", dayKey: "tue" },
  { value: "WED", dayKey: "wed" },
  { value: "THU", dayKey: "thu" },
  { value: "FRI", dayKey: "fri" },
  { value: "SAT", dayKey: "sat" },
  { value: "SUN", dayKey: "sun" },
]

const DEFAULT_WORKING_DAYS: WorkDay[] = ["MON", "TUE", "WED", "THU", "FRI"]

export function HolidayCalendarTab() {
  const { t } = useTranslation("settings")

  /* ── Holidays SWR ──────────────────────────────────────── */
  const { data: holidayData, isLoading: holidayLoading, error: holidayError, mutate: reloadHolidays } = useSWR(
    "settings.holiday-calendar.list",
    () => HolidayCalendarApi.list({ offset: { limit: 100, skip: 0 } }),
    { revalidateOnFocus: false },
  )
  const rows = holidayData?.rows ?? []
  const mutations = useSettingsMutations()

  /* ── Working-schedule SWR ──────────────────────────────── */
  const { data: scheduleData, isLoading: scheduleLoading, mutate: revalidateSchedule } = useSWR(
    ["governance-config"],
    () => GovernanceConfigApi.get(),
    { revalidateOnFocus: false },
  )

  /* ── Schedule form ─────────────────────────────────────── */
  const scheduleForm = useForm({
    initialValues: {
      workingDays: DEFAULT_WORKING_DAYS as WorkDay[],
      workingHoursStart: "09:00",
      workingHoursEnd: "18:00",
      orderCutoffTime: "17:00",
    },
  })

  useEffect(() => {
    if (!scheduleData) return
    scheduleForm.setValues({
      workingDays: (scheduleData.workingDays ?? DEFAULT_WORKING_DAYS) as WorkDay[],
      workingHoursStart: scheduleData.workingHours?.start ?? "09:00",
      workingHoursEnd: scheduleData.workingHours?.end ?? "18:00",
      orderCutoffTime: scheduleData.orderCutoffTime ?? "17:00",
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleData])

  const handleScheduleSave = async () => {
    await mutations.governance.update({
      workingDays: scheduleForm.values.workingDays,
      workingHours: {
        start: scheduleForm.values.workingHoursStart,
        end: scheduleForm.values.workingHoursEnd,
      },
      orderCutoffTime: scheduleForm.values.orderCutoffTime,
    })
    await revalidateSchedule()
  }

  /* ── Holiday CRUD state ────────────────────────────────── */
  const [editTarget, setEditTarget] = useState<IBusinessHoliday | null>(null)
  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false)

  const handleEdit = (row: IBusinessHoliday) => {
    setEditTarget(row)
    openEdit()
  }

  const handleDelete = (row: IBusinessHoliday) => {
    modals.openConfirmModal({
      title: t("common.confirmDelete", { name: row.name }),
      children: (
        <Text size="sm">
          <Text span fw={600}>{row.name}</Text>{" "}
          {t("holiday.holidays.fields.name.label")}
        </Text>
      ),
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => mutations.holiday.remove(row.id),
    })
  }

  return (
    <Stack gap="md">
      {/* ── Card 1: Ажлын хуваарь ──────────────────────────── */}
      <Card withBorder radius="md" p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={4}>{t("holiday.workingSchedule.title")}</Title>
            <Anchor
              href="/users#permissions"
              target="_blank"
              size="sm"
            >
              {t("holiday.workingSchedule.permissionAnchor")}
            </Anchor>
          </Group>

          {scheduleLoading ? (
            <Group justify="center" py="sm">
              <Loader size="sm" color="brand" />
            </Group>
          ) : (
            <Stack gap="sm">
              {/* Working days chip group */}
              <Box>
                <Text size="sm" fw={500} mb={6}>
                  {t("holiday.workingSchedule.fields.workingDays.label")}
                </Text>
                <Text size="sm" c="gray.7" mb={6}>
                  {t("holiday.workingSchedule.fields.workingDays.description")}
                </Text>
                <Chip.Group
                  multiple
                  value={scheduleForm.values.workingDays}
                  onChange={(val) =>
                    scheduleForm.setFieldValue("workingDays", val as WorkDay[])
                  }
                >
                  <Group gap="xs">
                    {ALL_DAY_VALUES.map((d) => (
                      <Chip key={d.value} value={d.value} size="md">
                        {t(`daysShort.${d.dayKey}`, { ns: "common" })}
                      </Chip>
                    ))}
                  </Group>
                </Chip.Group>
              </Box>

              {/* Working hours + cutoff in a row */}
              <Group grow gap="sm">
                <TimeInput
                  label={t("holiday.workingSchedule.fields.workingHoursStart.label")}
                  {...scheduleForm.getInputProps("workingHoursStart")}
                />
                <TimeInput
                  label={t("holiday.workingSchedule.fields.workingHoursEnd.label")}
                  {...scheduleForm.getInputProps("workingHoursEnd")}
                />
                <TimeInput
                  label={t("holiday.workingSchedule.fields.orderCutoffTime.label")}
                  description={t("holiday.workingSchedule.fields.orderCutoffTime.description")}
                  {...scheduleForm.getInputProps("orderCutoffTime")}
                />
              </Group>

              <Group justify="flex-end">
                <SettingsButton
                  code={SETTINGS_WRITE.governance}
                  loading={mutations.submitting}
                  onClick={handleScheduleSave}
                >
                  {t("common.save")}
                </SettingsButton>
              </Group>
            </Stack>
          )}
        </Stack>
      </Card>

      {/* ── Card 2: Баярын жагсаалт ────────────────────────── */}
      <Card withBorder radius="md" p="md">
        <Group justify="space-between" mb="md">
          <Title order={4}>{t("holiday.holidays.title")}</Title>
          <SettingsButton code={SETTINGS_WRITE.governance} leftSection={<PlusIcon size={18} />} onClick={openCreate}>
            {t("holiday.holidays.actions.newHoliday")}
          </SettingsButton>
        </Group>

        {holidayLoading ? (
          <ListState state="loading" columns={6} />
        ) : holidayError ? (
          <ListState state="error" title={t("common.error")} description={t("common.loadError")} onRetry={() => reloadHolidays()} />
        ) : rows.length === 0 ? (
          <ListState state="empty" title={t("common.empty")} description={t("common.emptyHint")} />
        ) : (
          <Table striped withTableBorder highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={120}>{t("common.actions")}</Table.Th>
                <Table.Th>{t("holiday.holidays.fields.holidayDate.label")}</Table.Th>
                <Table.Th>{t("holiday.holidays.fields.name.label")}</Table.Th>
                <Table.Th>{t("holiday.holidays.fields.description.label")}</Table.Th>
                <Table.Th>{t("holiday.holidays.fields.recurrence.label")}</Table.Th>
                <Table.Th>{t("holiday.holidays.fields.isFullDay.label")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>
                    <SettingsActionCell>
                      <SettingsRowAction code={SETTINGS_WRITE.governance} icon={<PencilSimpleIcon />} label={t("common.edit")} onClick={() => handleEdit(r)} />
                      <SettingsRowAction code={SETTINGS_WRITE.governance} icon={<TrashIcon />} label={t("common.delete")} onClick={() => handleDelete(r)} />
                    </SettingsActionCell>
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={dateFormat(r.holidayDate)} fontWeight={500} />
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={r.name} fontWeight={500} />
                  </Table.Td>
                  <Table.Td>
                    <RenderText text={r.description ?? "-"} color="dimmed" />
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="gray" size="lg" tt="none">
                      {r.recurrence === "YEARLY"
                        ? t("holiday.holidays.options.YEARLY")
                        : t("holiday.holidays.options.ONE_TIME")}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={r.isFullDay ? "green" : "gray"} size="lg" tt="none">
                      {r.isFullDay ? t("common.yes") : t("common.no")}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <HolidayFormModal opened={createOpen} onClose={closeCreate} />
      <HolidayFormModal
        opened={editOpen}
        onClose={closeEdit}
        initial={editTarget}
        key={`edit-${editTarget?.id ?? "none"}`}
      />
    </Stack>
  )
}
